// SF1 + SF2 — Seasonal Pattern & Index. Merged, as the PRD directs: they are
// one computation with one presentation.
//
// THE LADDER IS THE FEATURE. What a seller sees depends entirely on how many
// complete cycles their history holds, and for a young account the honest
// answer is "not enough to say anything about the year". Below twelve months
// this component shows monthly totals and never uses the word seasonal — not
// pedantry, but because a seller who reads it, even in a denial, comes away
// believing they were shown something about their year.
//
// The chart shows EXPECTED SALES, never deviations, so no bar points downward
// and a weak month reads as below normal rather than as a failure.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../Seasonal.module.css';
import { getPattern } from '../services/seasonalApi';
import SyncProgress from '../../components/SyncProgress';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Monthly totals, for the tiers that get no pattern. Deliberately plain: this
// is a list of what happened, and must not look like a projection.
const MonthlyActuals = ({ months, values, partial }) => {
  const peak = Math.max(...values.filter((v) => v !== null), 1);
  return (
    <div className={local.chart}>
      {months.map((label, index) => {
        const value = values[index];
        const isPartial = (partial || []).includes(label);
        return (
          <div key={label} className={`${local.col} ${isPartial ? local.partial : ''}`}
            title={isPartial ? `${label} — part month, not a full month of sales` : label}>
            <span className={local.barValue}>
              {value === null ? '' : inr.format(Math.round(value))}
            </span>
            <span className={`${local.bar} ${local.normal}`}
              style={{ height: `${((value || 0) / peak) * 100}%` }} />
            <span className={local.barLabel}>{label.slice(2)}</span>
          </div>
        );
      })}
    </div>
  );
};

const SeasonalPattern = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getPattern(token));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const pattern = data?.pattern;
  const decomposed = Array.isArray(pattern) && pattern.length > 0;
  // Below 24 months the heading must not promise a pattern the data cannot
  // support, so the title itself changes with the tier.
  const title = decomposed ? 'The shape of your sales year' : 'Monthly sales';

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>{title}</h2>
        {data?.available && (
          <span className={local.tierChip}>
            {data.months_of_history} full months
            {data.cycles > 0 && ` · ${data.cycles} complete ${data.cycles === 1 ? 'cycle' : 'cycles'}`}
          </span>
        )}
      </div>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.progress ? 'Sync in progress'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load monthly sales'}
            </strong>
            <div>{error.message}</div>
            <SyncProgress progress={error.progress} />
          </div>
        </div>
      )}

      {loading && !data && <div className={local.emptyCell}>Loading…</div>}

      {/* The refusal, worded so it cannot be mistaken for a weak year. */}
      {data && data.reason && (
        <div className={`${local.banner} ${local.bannerWarn}`}>
          <FiInfo className={local.bannerIcon} />
          <div>
            <strong>Not enough history to separate the year from the trend</strong>
            <div>{data.reason}</div>
          </div>
        </div>
      )}

      {data?.available && !decomposed && data.observed_months?.length > 0 && (
        <MonthlyActuals months={data.observed_months} values={data.observed_all}
          partial={data.partial_months} />
      )}

      {decomposed && (
        <>
          <p className={local.muted}>
            Expected sales for each month at your current trend level, with
            growth and festival spikes taken out. A normal month is
            {' '}<strong>{inr.format(Math.round(data.normal_month_units))} units</strong>.
          </p>
          <div className={local.chart}>
            {pattern.map((month) => {
              const peak = Math.max(...pattern.map((m) => m.expected_units || 0), 1);
              return (
                <div key={month.month} className={local.col}
                  title={`${month.name}: index ${month.index} — ${month.label}`}>
                  <span className={local.barValue}>
                    {inr.format(Math.round(month.expected_units))}
                  </span>
                  <span className={`${local.bar} ${local[month.band]}`}
                    style={{ height: `${((month.expected_units || 0) / peak) * 100}%` }} />
                  <span className={local.barLabel}>{SHORT[month.month - 1]}</span>
                </div>
              );
            })}
          </div>
          <div className={local.breadcrumb}>
            <span>
              Strongest <strong>{data.strongest.name}</strong> ({data.strongest.index})
            </span>
            <span className={local.crumbSep}>·</span>
            <span>
              Weakest <strong>{data.weakest.name}</strong> ({data.weakest.index})
            </span>
            <span className={local.crumbTotal}>
              Confidence {data.confidence.level} — {data.confidence.cycles} cycles
            </span>
          </div>
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SeasonalPattern;
